import { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ExcludedCreateEditModal from './ExcludedCreateEditModal';
import ConfirmModal from '../../../../../components/ConfirmModal/ConfirmModal';
import {
  getExcludedPlaintiffsPaged,
  createExcludedPlaintiff,
  updateExcludedPlaintiff,
  deleteExcludedPlaintiff,
} from '../../../../../services/excludedPlaintiffServices';
import { useLanguage } from '../../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../../utils/createScopedI18n';
import { configExcludedI18n } from './i18n';
import { useSnackbar } from 'context/SnackbarContext';
import './configExcluded.scss';

const FILTER_VALUES = {
  active: 'active',
  inactive: 'inactive',
};

const normalizeStatus = (value) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();

  if (['inactive', 'inactivo', 'inactivos'].includes(normalized)) {
    return FILTER_VALUES.inactive;
  }

  return FILTER_VALUES.active;
};

const normalizeFilterValue = (value) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();

  if (['active', 'activo', 'activos'].includes(normalized)) {
    return FILTER_VALUES.active;
  }
  if (['inactive', 'inactivo', 'inactivos'].includes(normalized)) {
    return FILTER_VALUES.inactive;
  }

  return null;
};

const toBackendStatusFilter = (value) =>
  value === FILTER_VALUES.inactive ? 'Inactivos' : 'Activos';

export default function ConfigExcluded() {
  const { language } = useLanguage();
  const excludedLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: configExcludedI18n });
  const { showSnackbar } = useSnackbar();

  const location = useLocation();
  const { DesignSystemComponents } = useOutletContext() ?? {};
  const { Button, Input, Table, Badge, IconButton, Modal, Dropdown } =
    DesignSystemComponents ?? {};

  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [statusFilterValue, setStatusFilterValue] = useState(FILTER_VALUES.active);
  const stateStatus = location.state?.status ?? '';

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [formValue, setFormValue] = useState({
    id: null,
    name: '',
    status: FILTER_VALUES.active,
  });

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => {
    configExcludedI18n.changeLanguage(excludedLanguage);
  }, [excludedLanguage]);

  const statusOptions = useMemo(
    () => [
      { label: t('config_excluded_filter_active'), value: FILTER_VALUES.active },
      {
        label: t('config_excluded_filter_inactive'),
        value: FILTER_VALUES.inactive,
      },
    ],
    [t]
  );

  const selectedStatusFilter =
    statusOptions.find((option) => option.value === statusFilterValue) ||
    statusOptions[0];

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const mapSortKeyToBackend = (key) => {
    switch (key) {
      case 'name':
        return 'name';
      case 'status':
        return 'isEnabled';
      default:
        return null;
    }
  };

  const handleServerSortChange = ({ sortKey: key, direction }) => {
    const normalizedDirection =
      typeof direction === 'string' ? direction.toLowerCase() : direction;
    setSortKey(normalizedDirection ? key : null);
    setSortDirection(normalizedDirection ?? null);
    setPage(0);
  };

  const backendSortKey = mapSortKeyToBackend(sortKey) || 'name';

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const backendPage = page + 1;

      const response = await getExcludedPlaintiffsPaged(
        backendPage,
        pageSize,
        debouncedSearch,
        toBackendStatusFilter(statusFilterValue),
        sortDirection,
        backendSortKey
      );

      const mappedExcluded = (response.data || []).map((excluded) => ({
        id: excluded.id,
        name: excluded.name || '',
        status: excluded.isEnabled
          ? FILTER_VALUES.active
          : FILTER_VALUES.inactive,
      }));

      setRows(mappedExcluded);
      setTotalRecords(response.total || 0);
    } catch (error) {
      console.error('Error loading excluded plaintiffs:', error);
      showSnackbar(t('config_excluded_error_load'), 'error');
      setRows([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    page,
    pageSize,
    debouncedSearch,
    statusFilterValue,
    sortDirection,
    backendSortKey,
    // showSnackbar and t are intentionally excluded to avoid refetches when snackbar queue changes.
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!stateStatus) return;
    const normalized = normalizeFilterValue(stateStatus);
    if (normalized) {
      setStatusFilterValue(normalized);
      setPage(0);
    }
  }, [stateStatus]);

  const statusColor = (status) =>
    normalizeStatus(status) === FILTER_VALUES.active ? 'success' : 'light';

  const openCreate = () => {
    setFormMode('create');
    setFormValue({ id: null, name: '', status: FILTER_VALUES.active });
    setIsFormOpen(true);
  };

  const openEdit = (row) => {
    setFormMode('edit');
    setFormValue({ ...row, status: normalizeStatus(row?.status) });
    setIsFormOpen(true);
  };

  const requestDelete = (row) => {
    setToDelete(row);
    setIsDeleteOpen(true);
  };

  const saveForm = async (updatedValue) => {
    const dataToSave = updatedValue || formValue;
    const { name, status } = dataToSave;

    if (!name?.trim()) {
      showSnackbar(t('config_excluded_required_fields'), 'error');
      return;
    }

    try {
      if (formMode === 'create') {
        await createExcludedPlaintiff({ name });
        showSnackbar(t('config_excluded_success_created'), 'success');
      } else {
        await updateExcludedPlaintiff(dataToSave.id, {
          name,
          status: status === FILTER_VALUES.active ? 'Activo' : 'Inactivo',
        });
        showSnackbar(t('config_excluded_success_updated'), 'success');
      }

      setIsFormOpen(false);
      await fetchData();
    } catch (error) {
      console.error('Error saving excluded plaintiff:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.Message ||
        error.response?.data ||
        error.customMessage ||
        t('config_excluded_error_save');

      showSnackbar(errorMessage, 'error');
    }
  };

  const confirmDelete = async () => {
    if (!toDelete?.id) return;

    try {
      await deleteExcludedPlaintiff(toDelete.id);

      setIsDeleteOpen(false);
      setToDelete(null);
      showSnackbar(t('config_excluded_success_deleted'), 'success');
      await fetchData();
    } catch (error) {
      console.error('Error deleting excluded plaintiff:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.Message ||
        error.response?.data ||
        error.customMessage ||
        t('config_excluded_error_delete');

      showSnackbar(errorMessage, 'error');
    }
  };

  if (!Button || !Input || !Table || !Badge || !IconButton || !Modal || !Dropdown) return null;

  return (
    <div>
      <div className='row'>
        <div className='col-5'>
          <Input
            icon='search'
            name='searchExcluded'
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('config_excluded_search_placeholder')}
            type='text'
            value={search}
            disabled={isLoading}
          />
        </div>

        <div className='col-4'>
          <Dropdown
            options={statusOptions}
            value={selectedStatusFilter}
            onChange={(option) => {
              setStatusFilterValue(option?.value || FILTER_VALUES.active);
              setPage(0);
            }}
            placeholder={t('config_excluded_filter_active')}
            width='100%'
            isDisabled={isLoading}
          />
        </div>

        <div className='col-3'>
          <div className='d-flex justify-content-end'>
            <Button
              label={t('config_excluded_add_button')}
              size='sm'
              variant='primary'
              onClick={openCreate}
              disabled={isLoading}
            />
          </div>
        </div>
      </div>

      <div className='col-12'>
        <div className='notification-box mb-3'>
          <p className='caption'>
            <span className='fw-bold'>
              {t('config_excluded_notice_title')}
            </span>{' '}
            {t('config_excluded_notice_text')}
          </p>
        </div>
      </div>

      <Table
        columns={[
          { accessor: 'name', label: t('config_excluded_col_name'), sort: true },
          {
            accessor: 'status',
            label: t('config_excluded_col_status'),
            sort: true,
            render: (row) => (
              <Badge
                text={
                  normalizeStatus(row.status) === FILTER_VALUES.active
                    ? t('config_excluded_status_active')
                    : t('config_excluded_status_inactive')
                }
                color={statusColor(row.status)}
                size='sm'
              />
            ),
          },
          {
            label: t('config_excluded_col_actions'),
            align: 'right',
            render: (row) => (
              <div className='d-flex gap-2'>
                <IconButton
                  ariaLabel={t('config_excluded_action_edit')}
                  icon='pencil'
                  onClick={() => openEdit(row)}
                  size='md'
                  iconStroke='var(--primary-500)'
                  disabled={isLoading}
                />
                {normalizeStatus(row.status) === FILTER_VALUES.active && (
                  <IconButton
                    ariaLabel={t('config_excluded_action_delete')}
                    icon='trash'
                    onClick={() => requestDelete(row)}
                    size='md'
                    iconStroke='var(--error-500)'
                    disabled={isLoading}
                  />
                )}
              </div>
            ),
          },
        ]}
        data={rows}
        page={page}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(0);
        }}
        paginationMode='server'
        totalItems={totalRecords}
        paginator
        paginatorGroup={[
          { label: '10', value: 10 },
          { label: '20', value: 20 },
          { label: '50', value: 50 },
          { label: '100', value: 100 },
        ]}
        loading={isLoading}
        serverSortKey={sortKey}
        serverSortDirection={sortDirection}
        onServerSortChange={handleServerSortChange}
      />

      <ExcludedCreateEditModal
        Modal={Modal}
        Input={Input}
        Dropdown={Dropdown}
        show={isFormOpen}
        mode={formMode}
        value={formValue}
        onChange={setFormValue}
        onClose={() => setIsFormOpen(false)}
        onSave={saveForm}
      />

      <ConfirmModal
        Modal={Modal}
        show={isDeleteOpen}
        title={t('config_excluded_confirm_delete_title')}
        message={
          toDelete
            ? t('config_excluded_confirm_delete_message', { name: toDelete.name })
            : t('config_excluded_confirm_delete_fallback')
        }
        confirmLabel={t('config_excluded_confirm_delete_button')}
        cancelLabel={t('config_excluded_cancel')}
        confirmVariant='danger'
        onConfirm={confirmDelete}
        onClose={() => setIsDeleteOpen(false)}
      />
    </div>
  );
}
